/**
 * aeterna/neural/curiosity.ts — Information-Theoretic Curiosity Drive
 *
 * The missing soul of most companion AIs: they never *want* anything.
 * They react. They respond. They don't reach.
 *
 * This module gives the Aeterna genuine information-theoretic curiosity.
 * It tracks what it knows, what it doesn't know, and what learning
 * about would most reduce its uncertainty about the player.
 *
 * Two mechanisms:
 *
 * 1. **Prediction Error Curiosity** (Schmidhuber/Pathak):
 *    The Aeterna has a forward model that predicts the next embedding
 *    from the current one. When the prediction is wrong, that's
 *    interesting. The *magnitude* of the error is the curiosity signal.
 *    But we filter out noise: random gibberish produces high error
 *    but isn't genuinely interesting. We use the *learnable* prediction
 *    error — how much the forward model improves on this input.
 *
 * 2. **Information Gain (Bayesian Surprise)**:
 *    The Aeterna maintains a belief distribution over player "types"
 *    (inferred clusters of conversation style). Each message updates
 *    this distribution. The KL divergence between prior and posterior
 *    IS the information gain — how much the Aeterna learned about
 *    who this person is from this single message.
 *
 * Together, these produce:
 *   - Topics the Aeterna wants to explore ("you mentioned your sister
 *     earlier — I'm curious about that")
 *   - Questions the Aeterna genuinely wants answered (not scripted)
 *   - A sense of when the conversation is *boring* (low curiosity)
 *     vs *alive* (high curiosity)
 *
 * Zero dependencies. ~2KB weights. <0.3ms per step.
 */

import {
  zeros, dot, normalize, cosine, vadd, vsub, vscale,
  randomMatrix, mulberry32, gaussianSeeded, matvec, vtanh,
  softmax, sum, mean, variance,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Embedding dimensionality (must match embeddings.ts) */
const EMBED_DIM = 64;

/** Number of player type clusters for Bayesian surprise */
const N_CLUSTERS = 8;

/** Forward model hidden dim */
const FORWARD_HIDDEN = 32;

/** Curiosity signal EMA smoothing */
const CURIOSITY_ALPHA = 0.3;

/** How many recent topics to track for "gap" analysis */
const GAP_HISTORY_MAX = 50;

/** Minimum curiosity to generate a question */
const QUESTION_THRESHOLD = 0.6;

// ─── Forward Model ───────────────────────────────────────────────────────────
// Predicts the next message embedding from the current one.
// A single hidden-layer MLP: embed → 32 → embed
// Trained online via gradient descent on prediction error.

class ForwardModel {
  private W1: Float32Array; // FORWARD_HIDDEN × EMBED_DIM
  private b1: Float32Array; // FORWARD_HIDDEN
  private W2: Float32Array; // EMBED_DIM × FORWARD_HIDDEN
  private b2: Float32Array; // EMBED_DIM
  private lr = 0.005;

  // Store last prediction for learning
  private lastPrediction: Float32Array | null = null;
  private lastInput: Float32Array | null = null;
  private lastHidden: Float32Array | null = null;

  constructor(seed: number) {
    const s1 = 1 / Math.sqrt(EMBED_DIM);
    const s2 = 1 / Math.sqrt(FORWARD_HIDDEN);
    this.W1 = randomMatrix(FORWARD_HIDDEN, EMBED_DIM, s1, seed);
    this.b1 = zeros(FORWARD_HIDDEN);
    this.W2 = randomMatrix(EMBED_DIM, FORWARD_HIDDEN, s2, seed + 1);
    this.b2 = zeros(EMBED_DIM);
  }

  /** Predict the next embedding from the current one */
  predict(current: Float32Array): Float32Array {
    // Hidden = tanh(W1 · current + b1)
    const hidden = matvec(this.W1, FORWARD_HIDDEN, EMBED_DIM, current);
    vadd(hidden, this.b1, hidden);
    vtanh(hidden, hidden);

    // Output = W2 · hidden + b2
    const pred = matvec(this.W2, EMBED_DIM, FORWARD_HIDDEN, hidden);
    vadd(pred, this.b2, pred);
    normalize(pred);

    this.lastPrediction = pred;
    this.lastInput = current;
    this.lastHidden = hidden;

    return pred;
  }

  /**
   * Learn from the actual next embedding.
   * Returns the prediction error BEFORE the update (the curiosity signal).
   */
  learn(actual: Float32Array): number {
    if (!this.lastPrediction || !this.lastInput || !this.lastHidden) return 1;

    // Prediction error (cosine distance)
    const errorBefore = 1 - cosine(this.lastPrediction, actual);

    // Compute gradient: error = actual - prediction
    const delta = zeros(EMBED_DIM);
    for (let i = 0; i < EMBED_DIM; i++) {
      delta[i] = actual[i] - this.lastPrediction[i];
    }

    // Backprop through W2
    const dHidden = zeros(FORWARD_HIDDEN);
    for (let h = 0; h < FORWARD_HIDDEN; h++) {
      let grad = 0;
      for (let o = 0; o < EMBED_DIM; o++) {
        // Update W2
        this.W2[o * FORWARD_HIDDEN + h] += this.lr * delta[o] * this.lastHidden[h];
        grad += this.W2[o * FORWARD_HIDDEN + h] * delta[o];
      }
      // tanh derivative: (1 - h^2)
      const tanhDeriv = 1 - this.lastHidden[h] * this.lastHidden[h];
      dHidden[h] = grad * tanhDeriv;
    }

    // Backprop through W1
    for (let h = 0; h < FORWARD_HIDDEN; h++) {
      for (let i = 0; i < EMBED_DIM; i++) {
        this.W1[h * EMBED_DIM + i] += this.lr * dHidden[h] * this.lastInput[i];
      }
      this.b1[h] += this.lr * dHidden[h];
    }

    // Update output bias
    for (let o = 0; o < EMBED_DIM; o++) {
      this.b2[o] += this.lr * delta[o];
    }

    return errorBefore;
  }
}

// ─── Bayesian Surprise ───────────────────────────────────────────────────────
// Maintain a soft clustering of the player's conversation style.
// Each message updates the cluster assignment probabilities.
// KL divergence between prior and posterior = information gain.

class PlayerModel {
  /** Cluster centroids: N_CLUSTERS × EMBED_DIM */
  private centroids: Float32Array;
  /** Current belief (prior): probability of each cluster */
  private belief: Float32Array;
  /** Count of messages assigned to each cluster */
  private counts: Float32Array;
  /** Total messages seen */
  private totalMessages = 0;

  constructor(seed: number) {
    // Initialize centroids randomly (will be overwritten by first messages)
    this.centroids = randomMatrix(N_CLUSTERS, EMBED_DIM, 0.5, seed);
    this.belief = new Float32Array(N_CLUSTERS).fill(1 / N_CLUSTERS);
    this.counts = zeros(N_CLUSTERS);
  }

  /**
   * Update belief with a new message embedding.
   * Returns the information gain (KL divergence prior → posterior).
   */
  update(embedding: Float32Array): number {
    this.totalMessages++;

    // Compute similarity to each centroid
    const sims = zeros(N_CLUSTERS);
    for (let k = 0; k < N_CLUSTERS; k++) {
      const centroid = this.centroids.subarray(k * EMBED_DIM, (k + 1) * EMBED_DIM);
      sims[k] = cosine(embedding, centroid);
    }

    // Posterior: prior × likelihood (softmax of similarities)
    const logits = zeros(N_CLUSTERS);
    for (let k = 0; k < N_CLUSTERS; k++) {
      logits[k] = Math.log(this.belief[k] + 1e-10) + sims[k] * 5;
    }
    const posterior = softmax(logits);

    // KL divergence: Σ posterior * log(posterior / prior)
    let kl = 0;
    for (let k = 0; k < N_CLUSTERS; k++) {
      if (posterior[k] > 1e-10) {
        kl += posterior[k] * Math.log(posterior[k] / (this.belief[k] + 1e-10));
      }
    }

    // Update centroids: move closest centroid toward this embedding
    const bestK = argmaxF32(posterior);
    const centroidOffset = bestK * EMBED_DIM;
    const alpha = 1 / (this.counts[bestK] + 1);
    for (let d = 0; d < EMBED_DIM; d++) {
      this.centroids[centroidOffset + d] =
        (1 - alpha) * this.centroids[centroidOffset + d] + alpha * embedding[d];
    }
    this.counts[bestK]++;

    // Update belief (EMA toward posterior)
    for (let k = 0; k < N_CLUSTERS; k++) {
      this.belief[k] = 0.9 * this.belief[k] + 0.1 * posterior[k];
    }

    return kl;
  }

  /** Get the current belief distribution */
  getBelief(): Float32Array { return this.belief; }

  /** Entropy of current belief (high = uncertain about player type) */
  beliefEntropy(): number {
    let h = 0;
    for (let k = 0; k < N_CLUSTERS; k++) {
      const p = this.belief[k];
      if (p > 1e-10) h -= p * Math.log(p);
    }
    return h;
  }

  /** Which cluster is the player most likely in? */
  dominantCluster(): number {
    return argmaxF32(this.belief);
  }
}

// ─── Topic Gap Analysis ──────────────────────────────────────────────────────
// Track which "regions" of embedding space have been explored.
// Gaps = regions the player has never discussed but are adjacent
// to things they care about.

interface TopicGap {
  /** The unexplored region centroid */
  direction: Float32Array;
  /** How "interesting" this gap is (proximity to explored topics × lack of coverage) */
  interest: number;
  /** Suggested probe category */
  category: string;
}

// ─── Curiosity Drive ─────────────────────────────────────────────────────────

export interface CuriosityState {
  /** Prediction error curiosity (0-1, smoothed) */
  predictionCuriosity: number;
  /** Bayesian surprise (information gain, nats) */
  informationGain: number;
  /** Combined curiosity signal */
  overallCuriosity: number;
  /** Is the Aeterna curious enough to ask a question? */
  wantsToAsk: boolean;
  /** Belief entropy (uncertainty about player type) */
  playerUncertainty: number;
  /** How many unique topic regions have been explored */
  topicCoverage: number;
  /** Suggested question direction (if wantsToAsk) */
  questionDirection: string;
}

export class CuriosityDrive {
  private forward: ForwardModel;
  private playerModel: PlayerModel;

  /** Smoothed curiosity signals */
  private smoothPredCuriosity = 0;
  private smoothInfoGain = 0;

  /** Topic coverage: quantized embedding history for gap detection */
  private exploredTopics: Float32Array[] = [];
  private topicLabels: string[] = [];

  /** Question generation state */
  private lastQuestionTime = 0;
  private questionCooldown = 30_000; // 30 seconds between questions

  /** Curiosity trajectory for narrative integration */
  private curiosityHistory: number[] = [];
  private maxHistory = 100;

  /** Category heuristics for gap labeling */
  private static readonly CATEGORIES = [
    { keywords: ["feel", "happy", "sad", "emotion", "mood", "heart"], label: "feelings" },
    { keywords: ["friend", "family", "people", "someone", "they", "together"], label: "relationships" },
    { keywords: ["room", "furniture", "cozy", "decorate", "place", "home"], label: "home" },
    { keywords: ["think", "believe", "mean", "why", "wonder", "real"], label: "philosophy" },
    { keywords: ["play", "game", "fun", "build", "create", "explore"], label: "play" },
    { keywords: ["remember", "before", "used", "when", "long", "ago"], label: "memories" },
    { keywords: ["want", "wish", "hope", "dream", "future", "plan"], label: "aspirations" },
    { keywords: ["day", "morning", "night", "today", "weather", "time"], label: "daily life" },
  ];

  constructor(seed = 7777) {
    this.forward = new ForwardModel(seed);
    this.playerModel = new PlayerModel(seed + 100);
  }

  /**
   * Process a new message embedding. Returns the curiosity state.
   *
   * Call AFTER embedding is computed but BEFORE Hopfield storage,
   * so the forward model can learn from the prediction error.
   */
  process(embedding: Float32Array, text: string): CuriosityState {
    // 1. Forward model: predict this embedding from the last one, learn from error
    const predictionError = this.forward.learn(embedding);
    this.smoothPredCuriosity =
      CURIOSITY_ALPHA * predictionError + (1 - CURIOSITY_ALPHA) * this.smoothPredCuriosity;

    // Set up prediction for next step
    this.forward.predict(embedding);

    // 2. Bayesian surprise: how much did we learn about the player?
    const infoGain = this.playerModel.update(embedding);
    this.smoothInfoGain =
      CURIOSITY_ALPHA * infoGain + (1 - CURIOSITY_ALPHA) * this.smoothInfoGain;

    // 3. Topic coverage
    this.recordTopic(embedding, text);
    const topicCoverage = this.exploredTopics.length;

    // 4. Combined curiosity (geometric mean — both signals must be present)
    const overallCuriosity = Math.sqrt(
      Math.max(0, this.smoothPredCuriosity) *
      Math.max(0, this.smoothInfoGain)
    );

    // 5. Question generation
    const now = Date.now();
    const cooldownOk = now - this.lastQuestionTime > this.questionCooldown;
    const wantsToAsk = overallCuriosity > QUESTION_THRESHOLD && cooldownOk;
    let questionDirection = "";

    if (wantsToAsk) {
      const gap = this.findMostInterestingGap(embedding);
      questionDirection = gap?.category ?? "general";
      this.lastQuestionTime = now;
    }

    // Track history
    this.curiosityHistory.push(overallCuriosity);
    if (this.curiosityHistory.length > this.maxHistory) {
      this.curiosityHistory.shift();
    }

    const playerUncertainty = this.playerModel.beliefEntropy();

    return {
      predictionCuriosity: this.smoothPredCuriosity,
      informationGain: this.smoothInfoGain,
      overallCuriosity,
      wantsToAsk,
      playerUncertainty,
      topicCoverage,
      questionDirection,
    };
  }

  /** Record a topic embedding for coverage tracking */
  private recordTopic(embedding: Float32Array, text: string): void {
    // Only add if sufficiently different from existing topics
    const threshold = 0.85;
    for (const t of this.exploredTopics) {
      if (cosine(embedding, t) > threshold) return;
    }

    this.exploredTopics.push(new Float32Array(embedding));

    // Label it
    const lower = text.toLowerCase();
    for (const cat of CuriosityDrive.CATEGORIES) {
      if (cat.keywords.some(kw => lower.includes(kw))) {
        this.topicLabels.push(cat.label);
        return;
      }
    }
    this.topicLabels.push("general");

    // Limit size
    if (this.exploredTopics.length > GAP_HISTORY_MAX) {
      this.exploredTopics.shift();
      this.topicLabels.shift();
    }
  }

  /**
   * Find the most interesting unexplored "gap" in conversation space.
   * A gap is a direction that is:
   *   1. Close to things the player talks about (adjacent interest)
   *   2. But hasn't been directly discussed (novelty)
   *   3. In a labeled category (so we can generate a question)
   */
  private findMostInterestingGap(currentEmbed: Float32Array): TopicGap | null {
    if (this.exploredTopics.length < 3) return null;

    // Compute centroid of explored topics
    const centroid = zeros(EMBED_DIM);
    for (const t of this.exploredTopics) {
      for (let i = 0; i < EMBED_DIM; i++) centroid[i] += t[i];
    }
    for (let i = 0; i < EMBED_DIM; i++) centroid[i] /= this.exploredTopics.length;
    normalize(centroid);

    // Try several random directions near the centroid
    let bestGap: TopicGap | null = null;
    let bestInterest = 0;

    for (let attempt = 0; attempt < 10; attempt++) {
      // Perturb centroid
      const direction = zeros(EMBED_DIM);
      for (let i = 0; i < EMBED_DIM; i++) {
        direction[i] = centroid[i] + (Math.random() - 0.5) * 0.5;
      }
      normalize(direction);

      // Proximity to explored topics
      let maxSim = 0;
      for (const t of this.exploredTopics) {
        const s = cosine(direction, t);
        if (s > maxSim) maxSim = s;
      }

      // Interest = close enough to be relevant, but not already covered
      // Sweet spot: similarity 0.3-0.6 to nearest explored topic
      const relevance = Math.max(0, 1 - Math.abs(maxSim - 0.45) / 0.3);
      const noveltyComponent = 1 - maxSim;
      const interest = relevance * noveltyComponent;

      if (interest > bestInterest) {
        bestInterest = interest;
        bestGap = {
          direction: new Float32Array(direction),
          interest,
          category: this.categorizeDirection(direction),
        };
      }
    }

    return bestGap;
  }

  /** Categorize a direction vector by finding the closest explored topic's category */
  private categorizeDirection(direction: Float32Array): string {
    let bestSim = -1;
    let bestLabel = "general";

    for (let i = 0; i < this.exploredTopics.length; i++) {
      const sim = cosine(direction, this.exploredTopics[i]);
      if (sim > bestSim) {
        bestSim = sim;
        bestLabel = this.topicLabels[i] ?? "general";
      }
    }

    return bestLabel;
  }

  // ─── Queries ───────────────────────────────────────────────────────────

  /** Get the curiosity trend (rising = conversation getting more interesting) */
  getCuriosityTrend(): number {
    const h = this.curiosityHistory;
    if (h.length < 6) return 0;
    const recent = h.slice(-3).reduce((a, b) => a + b, 0) / 3;
    const older = h.slice(-6, -3).reduce((a, b) => a + b, 0) / 3;
    return recent - older;
  }

  /** Is the conversation getting boring? */
  isBoring(): boolean {
    if (this.curiosityHistory.length < 10) return false;
    const recent5 = this.curiosityHistory.slice(-5);
    return recent5.every(c => c < 0.2);
  }

  /** Is the conversation alive and interesting? */
  isAlive(): boolean {
    if (this.curiosityHistory.length < 5) return true;
    const recent5 = this.curiosityHistory.slice(-5);
    return recent5.some(c => c > 0.5);
  }

  /** Get all explored topic labels */
  getExploredTopics(): string[] {
    return [...new Set(this.topicLabels)];
  }

  /** Get the player model belief distribution */
  getPlayerBelief(): Float32Array {
    return this.playerModel.getBelief();
  }
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function argmaxF32(arr: Float32Array): number {
  let best = 0;
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] > arr[best]) best = i;
  }
  return best;
}
